import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, Download, Edit3, Eye, FileText, History, Lock, MoreHorizontal, Plus, Trash2, Unlock, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import flowpiMoveIcon from "../assets/flowpi-move-icon.svg";
import StageMoveDialog from "../components/service/StageMoveDialog";
import ServiceLifecycleDialog from "../components/service/ServiceLifecycleDialog";
import ServiceExportDialog from "../components/service/ServiceExportDialog";
import ServiceStageHistoryDialog, {
  normalizeServiceStageHistory,
} from "../components/service/ServiceStageHistoryDialog";
import { FlowpiDialogSurface } from "../components/ui/flowpi-dialog";
import {
  ServiceBranchSearchRow,
  ServiceDvtBadge,
  ServiceListPagination,
  SERVICE_LIST_BRANCHES,
  formatServiceListDate,
} from "../components/service/ServiceListControls";
import { useAuth } from "../context/AuthContext";
import { formatApiError, getServiceErrorPayload } from "../lib/api";
import {
  checkServiceSerialNumber,
  createServiceTicket,
  getServiceTicket,
  getServiceTicketSqAction,
  getServiceTicketStageHistory,
  listActiveServiceDeviceModels,
  listServiceTickets,
  normalizeServiceTicketResponse,
  softDeleteServiceTicket,
} from "../lib/serviceApi";
import ServiceCostPresentation from "../components/service/ServiceCostPresentation";
import {
  DataValue,
  PassiveBadge,
} from "../components/ui/data-presentation";
import {
  activeDeviceModelOptions,
  modelNameForSelection,
} from "../lib/serviceFormOptions";
import {
  SERVICE_BRANCHES,
  SERVICE_BRANCH_LABELS,
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  SERVICE_DVT_STATUSES,
  SERVICE_DVT_STATUS_LABELS,
  SERVICE_STAGES,
  SERVICE_STAGE_LABELS,
} from "../lib/serviceConstants";
import {
  canDeleteServiceTicket,
  canShowServiceStandardMove,
  canViewDeletedServiceTickets,
  getServiceEditCapabilities,
} from "../lib/servicePermissions";
import {
  serviceTicketQuotationActionLabel,
  serviceTicketQuotationDestination,
  serviceTicketShowsQuotationAction,
} from "../lib/activeQuotation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import {
  ListErrorNotice,
  LIST_PAGE_SIZE_OPTIONS,
  ListTable,
  ListTableRefreshingRow,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnPriceRangeFilter,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  GlobalSearch,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_BRANCH = SERVICE_BRANCHES.JAKARTA;
const DEFAULT_STAGE = SERVICE_STAGES.INCOMING;
const QUEUE_AGING_THRESHOLD_MS = 4 * 24 * 60 * 60 * 1000;
const QUEUE_AGING_ROW_CLASS =
  "[&>td]:bg-destructive/10 hover:[&>td]:bg-destructive/10 dark:[&>td]:bg-destructive/15 dark:hover:[&>td]:bg-destructive/15";
const SOLD_FILTER_KEYS = Object.freeze([
  "date_in",
  "owner_name",
  "serial_number",
  "model",
  "service_cost_min",
  "service_cost_max",
  "dvt_status",
]);
const EMPTY_SOLD_FILTERS = Object.freeze(Object.fromEntries(SOLD_FILTER_KEYS.map((key) => [key, ""])));
const EMPTY_SOLD_DATE_RANGE = Object.freeze({ from: "", to: "" });
const SOLD_TABLE_GEOMETRY = Object.freeze([
  { key: "number", kind: "utility", width: 70 },
  { key: "date_in", kind: "fixed", width: 150 },
  { key: "owner_name", kind: "fill", minWidth: 230 },
  { key: "serial_number", kind: "fixed", width: 190 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "service_cost", kind: "fixed", width: 190 },
  { key: "dvt_status", kind: "fixed", width: 140 },
  { key: "actions", kind: "utility", width: 170 },
]);
const SOLD_DVT_OPTIONS = Object.values(SERVICE_DVT_STATUSES).map((value) => ({
  value,
  label: SERVICE_DVT_STATUS_LABELS[value],
}));

const STAGE_TABS = [
  SERVICE_STAGES.INCOMING,
  SERVICE_STAGES.QUEUE,
  SERVICE_STAGES.PENDING,
  SERVICE_STAGES.VALIDATION,
  SERVICE_STAGES.DELIVERY,
  SERVICE_STAGES.DONE,
];
const STAGE_CHEVRON_DEPTH_PX = 18;
const STAGE_VISIBLE_GAP_PX = 5;

const CREATE_STEPS = ["Category", "Device", "Owner", "Accessories"];

const ACCESSORY_OPTIONS = [
  { value: "imin_box", label: "iMin Box" },
  { value: "adapter_charger", label: "Adapter / Charger" },
  { value: "printer", label: "Printer" },
  { value: "scanner", label: "Scanner" },
  { value: "docking", label: "Docking" },
  { value: "second_screen", label: "Second Screen" },
  { value: "second_screen_cable", label: "Second Screen Cable" },
  { value: "manual_book_warranty_card", label: "Manual Book / Warranty Card" },
  { value: "other", label: "Other" },
];

const IMIN_STATUS_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "valid", label: "Valid" },
  { value: "invalid", label: "Invalid" },
];

const PURCHASE_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, "0"));
const PURCHASE_MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const CURRENT_YEAR = new Date().getFullYear();
const PURCHASE_YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 2020 + 1 },
  (_, index) => String(CURRENT_YEAR - index)
);
const WARRANTY_YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR + 3 - 2020 + 1 },
  (_, index) => String(CURRENT_YEAR + 3 - index)
);

function parseISODateOnly(value) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function isValidISODateOnly(value, { minYear = 2020, maxYear = CURRENT_YEAR } = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const [year, month, day] = value.split("-").map(Number);
  if (year < minYear || year > maxYear) return false;
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

function formatISODateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function splitISODateOnly(value) {
  if (!value) return { day: "", month: "", year: "" };
  const [year, month, day] = value.split("-");
  return { day: day || "", month: month || "", year: year || "" };
}

function calculateDvtPreview(purchaseDateValue, durationValue, dateIn = new Date()) {
  const purchaseDate = parseISODateOnly(purchaseDateValue);
  const durationYears = Number.parseInt(durationValue, 10);
  if (!purchaseDate || !Number.isInteger(durationYears) || durationYears < 1) {
    return { expiry_date: "", status: "" };
  }

  const expiryDate = new Date(purchaseDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + durationYears);

  const dateInDateOnly = new Date(dateIn.getFullYear(), dateIn.getMonth(), dateIn.getDate());
  const dayDifference = Math.floor((dateInDateOnly - purchaseDate) / 86400000);
  let status = "Out of Warranty";
  if (dayDifference >= 0 && dayDifference <= 7) {
    status = "DOA";
  } else if (dateInDateOnly <= expiryDate) {
    status = "In Warranty";
  }

  return {
    expiry_date: formatISODateOnly(expiryDate),
    status,
  };
}

function createInitialDraft() {
  return {
    category: "",
    branch: "",
    owner: {
      name: "",
      pic: "",
      contact: "",
      address: "",
    },
    device: {
      serial_number: "",
      stock_reference_status: "",
      sn_check_result: "",
      sn_check_serial_number: "",
      sn_check_warning: "",
      purchase_date_sn_message: "",
      purchase_date: "",
      purchase_date_day: "",
      purchase_date_month: "",
      purchase_date_year: "",
      model_selection: "",
      model: "",
      dvt_duration_years: "1",
      dvt_duration_locked: true,
      dvt_expiry_date: "",
      dvt_expiry_day: "",
      dvt_expiry_month: "",
      dvt_expiry_year: "",
      dvt_status: "",
      imin_expiry_date: "",
      imin_expiry_day: "",
      imin_expiry_month: "",
      imin_expiry_year: "",
      imin_status: "unknown",
      imin_warranty_sn_message: "",
      imin_warranty_lookup_result: "",
    },
    accessories: {
      items: [],
      other_details: "",
    },
    unknown_stock_reference_confirmed: false,
    notes: "",
  };
}

function normalizeBranch(value) {
  return Object.values(SERVICE_BRANCHES).includes(value) ? value : DEFAULT_BRANCH;
}

function normalizeStage(value) {
  return Object.values(SERVICE_STAGES).includes(value) ? value : DEFAULT_STAGE;
}

function normalizePage(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function normalizePageSize(value) {
  const parsed = Number.parseInt(value, 10);
  return LIST_PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

function isQueueTicketOverdue(ticket, selectedStage, now = Date.now()) {
  if (selectedStage !== SERVICE_STAGES.QUEUE || ticket?.stage !== SERVICE_STAGES.QUEUE) {
    return false;
  }

  const dateIn = new Date(ticket?.date_in).getTime();
  return Number.isFinite(dateIn) && now - dateIn > QUEUE_AGING_THRESHOLD_MS;
}

function readSoldFilters(searchParams) {
  return Object.fromEntries(SOLD_FILTER_KEYS.map((key) => [key, (searchParams.get(key) || "").trim()]));
}

function readSoldDateRange(searchParams) {
  const from = (searchParams.get("date_in_from") || "").trim();
  const to = (searchParams.get("date_in_to") || "").trim();
  return {
    from,
    to,
  };
}

function buildListSearchParams({ branch, stage, search, page, pageSize, soldFilters, dateRange }) {
  const params = new URLSearchParams();
  params.set("branch", normalizeBranch(branch));
  params.set("stage", normalizeStage(stage));
  params.set("page", String(normalizePage(page)));
  const normalizedPageSize = normalizePageSize(pageSize);
  if (normalizedPageSize !== DEFAULT_PAGE_SIZE) {
    params.set("page_size", String(normalizedPageSize));
  }
  const trimmedSearch = String(search || "").trim();
  if (trimmedSearch) params.set("search", trimmedSearch);
  SOLD_FILTER_KEYS.forEach((key) => {
    const value = String(soldFilters?.[key] || "").trim();
    if (value) params.set(key, value);
  });
  if (dateRange?.from) params.set("date_in_from", dateRange.from);
  if (dateRange?.to) params.set("date_in_to", dateRange.to);
  return params;
}

export default function ServiceTickets({ category }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isKnownCategory = Object.values(SERVICE_CATEGORIES).includes(category);
  const [tickets, setTickets] = useState([]);
  const [sqActions, setSqActions] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total: 0,
    total_pages: 0,
  });
  const selectedBranch = normalizeBranch(searchParams.get("branch"));
  const selectedStage = normalizeStage(searchParams.get("stage"));
  const page = normalizePage(searchParams.get("page"));
  const pageSize = normalizePageSize(searchParams.get("page_size"));
  const appliedSearch = (searchParams.get("search") || "").trim();
  const appliedSoldFilters = useMemo(
    () => (isKnownCategory ? readSoldFilters(searchParams) : EMPTY_SOLD_FILTERS),
    [isKnownCategory, searchParams]
  );
  const appliedSoldDateRange = useMemo(
    () => (isKnownCategory ? readSoldDateRange(searchParams) : EMPTY_SOLD_DATE_RANGE),
    [isKnownCategory, searchParams]
  );
  const [searchInput, setSearchInput] = useState(appliedSearch);
  const [soldFilterDrafts, setSoldFilterDrafts] = useState(appliedSoldFilters);
  const [soldDateRange, setSoldDateRange] = useState(appliedSoldDateRange);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [createDraft, setCreateDraft] = useState(createInitialDraft);
  const [createErrors, setCreateErrors] = useState({});
  const [createNotice, setCreateNotice] = useState("");
  const [snCheckLoading, setSnCheckLoading] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [deviceModels, setDeviceModels] = useState([]);
  const [deviceModelsLoading, setDeviceModelsLoading] = useState(false);
  const [deviceModelsError, setDeviceModelsError] = useState("");
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [listSuccessMessage, setListSuccessMessage] = useState("");
  const [moveTicket, setMoveTicket] = useState(null);
  const [moveLoadingTicketId, setMoveLoadingTicketId] = useState(null);
  const [deleteTicket, setDeleteTicket] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [historyTicket, setHistoryTicket] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRecords, setHistoryRecords] = useState([]);
  const snCheckRequestIdRef = useRef(0);
  const stockReferenceConfirmationRef = useRef(null);
  const historyRequestIdRef = useRef(0);
  const historyReturnFocusRef = useRef(null);
  const moreActionTriggerRefs = useRef(new Map());
  const ticketsRef = useRef([]);

  const deviceModelOptions = activeDeviceModelOptions(deviceModels);

  useEffect(() => {
    if (!createModalOpen) return undefined;
    let cancelled = false;
    async function loadDeviceModels() {
      setDeviceModelsLoading(true);
      setDeviceModelsError("");
      try {
        const models = await listActiveServiceDeviceModels();
        if (!cancelled) setDeviceModels(models);
      } catch (err) {
        if (!cancelled) {
          setDeviceModels([]);
          setDeviceModelsError(formatApiError(err));
        }
      } finally {
        if (!cancelled) setDeviceModelsLoading(false);
      }
    }

    loadDeviceModels();
    return () => {
      cancelled = true;
    };
  }, [createModalOpen]);

  useEffect(() => {
    if (
      moveTicket &&
      !canShowServiceStandardMove({ role: user?.role, stage: moveTicket.stage })
    ) {
      setMoveTicket(null);
    }
  }, [moveTicket, user?.role]);

  useEffect(() => {
    if (deleteTicket && !canDeleteServiceTicket(user?.role)) {
      setDeleteTicket(null);
      setDeleteError("");
    }
  }, [deleteTicket, user?.role]);

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    setSoldFilterDrafts(appliedSoldFilters);
    setSoldDateRange(appliedSoldDateRange);
  }, [appliedSoldDateRange, appliedSoldFilters]);

  useEffect(() => {
    if (!isKnownCategory) return;
    const next = buildListSearchParams({
      branch: selectedBranch,
      stage: selectedStage,
      search: appliedSearch,
      page,
      pageSize,
      soldFilters: appliedSoldFilters,
      dateRange: appliedSoldDateRange,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [appliedSearch, appliedSoldDateRange, appliedSoldFilters, isKnownCategory, page, pageSize, searchParams, selectedBranch, selectedStage, setSearchParams]);

  useEffect(() => {
    if (!isKnownCategory) return undefined;

    let cancelled = false;
    const hadRows = ticketsRef.current.length > 0;

    async function loadTickets() {
      setLoading(true);
      setError("");
      try {
        const params = {
          category,
          branch: selectedBranch,
          stage: selectedStage,
          page,
          page_size: pageSize,
        };
        if (appliedSearch) params.search = appliedSearch;
        SOLD_FILTER_KEYS.forEach((key) => {
          if (appliedSoldFilters[key]) params[key] = appliedSoldFilters[key];
        });
        if (appliedSoldDateRange.from) params.date_in_from = appliedSoldDateRange.from;
        if (appliedSoldDateRange.to) params.date_in_to = appliedSoldDateRange.to;

        const result = await listServiceTickets(params);
        const rows = Array.isArray(result?.data) ? result.data : [];
        const actionEntries = await Promise.all(
          rows.filter(serviceTicketShowsQuotationAction).map(async (ticket) => [
            ticket.id,
            await getServiceTicketSqAction(ticket.id),
          ])
        );
        if (cancelled) return;
        setSqActions(Object.fromEntries(actionEntries));
        ticketsRef.current = rows;
        setTickets(rows);
        setPagination({
          page: result?.pagination?.page ?? page,
          page_size: result?.pagination?.page_size ?? pageSize,
          total: result?.pagination?.total ?? 0,
          total_pages: result?.pagination?.total_pages ?? 0,
        });
      } catch (err) {
        if (cancelled) return;
        if (!hadRows) {
          ticketsRef.current = [];
          setTickets([]);
          setPagination({ page, page_size: pageSize, total: 0, total_pages: 0 });
        }
        setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTickets();

    return () => {
      cancelled = true;
    };
  }, [appliedSearch, appliedSoldDateRange, appliedSoldFilters, category, isKnownCategory, listRefreshKey, page, pageSize, selectedBranch, selectedStage]);

  if (!isKnownCategory) {
    return (
      <div className="space-y-6" data-testid="service-tickets-invalid">
        <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold mb-2">
          Service Tickets
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <p className="text-sm text-zinc-500">
            Select a valid Service category from the sidebar.
          </p>
        </div>
      </div>
    );
  }

  const categoryLabel = SERVICE_CATEGORY_LABELS[category];
  const stageSegmentClipPath = (index) => {
    if (index === 0) {
      return "polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)";
    }
    if (index === STAGE_TABS.length - 1) {
      return "polygon(0 0, 100% 0, 100% 100%, 0 100%, 18px 50%)";
    }
    return "polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%, 18px 50%)";
  };

  const stageSegmentStyle = (stage, index) => {
    const active = selectedStage === stage;
    return {
      backgroundColor: active ? "transparent" : "hsl(var(--muted))",
      backgroundImage: active ? "var(--service-stage-selected-background)" : "none",
      color: active ? "var(--service-stage-selected-foreground)" : "hsl(var(--muted-foreground))",
      clipPath: stageSegmentClipPath(index),
      marginLeft: index === 0 ? 0 : `-${STAGE_CHEVRON_DEPTH_PX}px`,
      width: index === 0 ? "100%" : `calc(100% + ${STAGE_CHEVRON_DEPTH_PX}px)`,
      paddingLeft: index === 0 ? "18px" : "34px",
      paddingRight: index === STAGE_TABS.length - 1 ? "18px" : "34px",
      zIndex: active ? STAGE_TABS.length + 1 : STAGE_TABS.length - index,
    };
  };

  const stageNumberStyle = (stage) => {
    const active = selectedStage === stage;
    return {
      backgroundColor: active ? "hsl(var(--primary-foreground) / 0.22)" : "hsl(var(--background))",
      color: active ? "var(--service-stage-selected-foreground)" : "hsl(var(--foreground))",
      borderColor: active ? "var(--service-stage-selected-foreground)" : "hsl(var(--border))",
    };
  };

  const changeBranch = (branch) => {
    if (branch === selectedBranch) return;
    setSearchParams(
      buildListSearchParams({
        branch,
        stage: selectedStage,
        search: appliedSearch,
        page: 1,
        pageSize,
        soldFilters: appliedSoldFilters,
        dateRange: appliedSoldDateRange,
      }),
      { replace: true }
    );
  };

  const changeStage = (stage) => {
    if (stage === selectedStage) return;
    setSearchParams(
      buildListSearchParams({
        branch: selectedBranch,
        stage,
        search: appliedSearch,
        page: 1,
        pageSize,
        soldFilters: appliedSoldFilters,
        dateRange: appliedSoldDateRange,
      }),
      { replace: true }
    );
  };

  const applySoldFilters = (nextFilters) => {
    if (!isKnownCategory) return;
    setSearchParams(
      buildListSearchParams({
        branch: selectedBranch,
        stage: selectedStage,
        search: appliedSearch,
        page: 1,
        pageSize,
        soldFilters: nextFilters,
        dateRange: appliedSoldDateRange,
      }),
      { replace: true }
    );
  };

  const updateSoldFilterDraft = (name, value) => {
    setSoldFilterDrafts((current) => ({ ...current, [name]: value }));
  };

  const applySoldFilter = (name, value) => {
    applySoldFilters({ ...appliedSoldFilters, [name]: value });
  };

  const applySoldGlobalSearch = (search) => {
    setSearchParams(
      buildListSearchParams({
        branch: selectedBranch,
        stage: selectedStage,
        search,
        page: 1,
        pageSize,
        soldFilters: appliedSoldFilters,
        dateRange: appliedSoldDateRange,
      }),
      { replace: true }
    );
  };

  const applySoldDateRange = (dateRange) => {
    setSearchParams(
      buildListSearchParams({
        branch: selectedBranch,
        stage: selectedStage,
        search: appliedSearch,
        page: 1,
        pageSize,
        soldFilters: appliedSoldFilters,
        dateRange,
      }),
      { replace: true }
    );
  };

  const clearSoldFilters = () => {
    setSearchInput("");
    setSoldFilterDrafts({ ...EMPTY_SOLD_FILTERS });
    setSoldDateRange({ ...EMPTY_SOLD_DATE_RANGE });
    setSearchParams(
      buildListSearchParams({
        branch: selectedBranch,
        stage: selectedStage,
        search: "",
        page: 1,
        pageSize,
        soldFilters: EMPTY_SOLD_FILTERS,
        dateRange: EMPTY_SOLD_DATE_RANGE,
      }),
      { replace: true }
    );
  };

  const currentListReturnUrl = () => {
    const params = buildListSearchParams({
      branch: selectedBranch,
      stage: selectedStage,
      search: appliedSearch,
      page,
      pageSize,
      soldFilters: appliedSoldFilters,
      dateRange: appliedSoldDateRange,
    });
    return `/service/${category}?${params.toString()}`;
  };

  const ticketRoute = (ticket, mode = "view") => {
    const suffix = mode === "edit" ? "/edit" : "";
    const returnUrl = encodeURIComponent(currentListReturnUrl());
    return `/service/tickets/${encodeURIComponent(ticket.id)}${suffix}?return=${returnUrl}`;
  };

  const openQuotation = async (ticket) => {
    try {
      const action = await getServiceTicketSqAction(ticket.id);
      const destination = serviceTicketQuotationDestination(action, ticket.id);
      if (destination) {
        navigate(destination);
        return;
      }
      toast.warning(
        "A quotation cannot be created while this Service Ticket is in its current condition."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openMoveDialog = async (ticket) => {
    if (moveLoadingTicketId) return;
    setMoveLoadingTicketId(ticket.id);
    try {
      const response = await getServiceTicket(ticket.id);
      const hydratedTicket = normalizeServiceTicketResponse(response);
      if (!hydratedTicket) throw new Error("Service ticket response was malformed.");
      setMoveTicket(hydratedTicket);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMoveLoadingTicketId(null);
    }
  };

  const openStageHistory = async (ticket, returnFocusNode = null) => {
    const requestId = historyRequestIdRef.current + 1;
    historyRequestIdRef.current = requestId;
    historyReturnFocusRef.current = returnFocusNode || moreActionTriggerRefs.current.get(ticket.id) || null;
    setHistoryTicket(ticket);
    setHistoryLoading(true);
    setHistoryError("");
    setHistoryRecords([]);
    try {
      const response = await getServiceTicketStageHistory(ticket.id, { page: 1, page_size: 100 });
      if (historyRequestIdRef.current !== requestId) return;
      setHistoryRecords(normalizeServiceStageHistory(response));
    } catch (err) {
      if (historyRequestIdRef.current !== requestId) return;
      setHistoryError(formatApiError(err));
    } finally {
      if (historyRequestIdRef.current === requestId) setHistoryLoading(false);
    }
  };

  const closeStageHistory = () => {
    historyRequestIdRef.current += 1;
    const returnFocus = historyReturnFocusRef.current;
    setHistoryTicket(null);
    setHistoryLoading(false);
    setHistoryError("");
    setHistoryRecords([]);
    queueMicrotask(() => returnFocus?.focus());
  };

  const deletedTicketsRoute = () => {
    const params = new URLSearchParams();
    params.set("branch", selectedBranch);
    params.set("page", "1");
    if (appliedSearch) params.set("search", appliedSearch);
    return `/service/${category}/deleted?${params.toString()}`;
  };

  const openDeleteDialog = (ticket) => {
    setDeleteError("");
    setDeleteTicket(ticket);
  };

  const closeDeleteDialog = () => {
    setDeleteTicket(null);
    setDeleteError("");
  };

  const confirmDeleteTicket = async (reason) => {
    if (!deleteTicket) return;
    setDeleteError("");
    try {
      await softDeleteServiceTicket(deleteTicket.id, { reason });
      const ticketNumber = deleteTicket.ticket_number;
      closeDeleteDialog();
      setListSuccessMessage(
        ticketNumber
          ? `Service ticket ${ticketNumber} deleted successfully.`
          : "Service ticket deleted successfully."
      );
      setListRefreshKey((current) => current + 1);
    } catch (err) {
      setDeleteError(formatApiError(err));
      throw err;
    }
  };

  const resetCreateDraft = () => {
    snCheckRequestIdRef.current += 1;
    setCreateDraft(createInitialDraft());
    setCreateStep(0);
    setCreateErrors({});
    setCreateNotice("");
    setSnCheckLoading(false);
    setCreateSubmitting(false);
  };

  const openCreateModal = () => {
    setCreateDraft(createInitialDraft());
    setCreateStep(0);
    setCreateErrors({});
    setCreateNotice("");
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    snCheckRequestIdRef.current += 1;
    setCreateModalOpen(false);
    resetCreateDraft();
  };

  const updateDraftField = (path, value) => {
    if (path[0] === "device" && path[1] === "serial_number") {
      snCheckRequestIdRef.current += 1;
      setSnCheckLoading(false);
    }
    setCreateDraft((current) => {
      const next = {
        ...current,
        owner: { ...current.owner },
        device: { ...current.device },
        accessories: {
          ...current.accessories,
          items: [...current.accessories.items],
        },
      };
      if (path.length === 1) {
        next[path[0]] = value;
      } else {
        next[path[0]][path[1]] = value;
      }
      if (path[0] === "device" && path[1] === "model_selection") {
        if (current.device.sn_check_result === "known") {
          next.device.model_selection = current.device.model_selection;
          next.device.model = current.device.model;
        } else {
          next.device.model = modelNameForSelection(deviceModelOptions, value);
        }
      }
      if (path[0] === "device" && path[1] === "serial_number") {
        next.device.stock_reference_status = "";
        next.device.sn_check_result = "";
        next.device.sn_check_serial_number = "";
        next.device.sn_check_warning = "";
        next.device.purchase_date_sn_message = "";
        next.device.imin_warranty_sn_message = "";
        next.device.imin_warranty_lookup_result = "";
        next.device.model = next.device.model_selection
          ? modelNameForSelection(deviceModelOptions, next.device.model_selection)
          : "";
        next.unknown_stock_reference_confirmed = false;
      }
      if (
        path[0] === "device" &&
        ["imin_status", "imin_expiry_date"].includes(path[1])
      ) {
        next.device.imin_warranty_sn_message = "";
        next.device.imin_warranty_lookup_result = "";
      }
      return next;
    });
    setCreateErrors((current) => {
      const key = path.join(".");
      const next = { ...current };
      delete next[key];
      if (path[0] === "device" && path[1] === "serial_number") {
        delete next["device.sn_check"];
        delete next["device.sn_check_api"];
        delete next.unknown_stock_reference_confirmed;
      }
      return next;
    });
    setCreateNotice("");
  };

  const updateDeviceDatePart = (targetField, part, value) => {
    setCreateDraft((current) => {
      const device = {
        ...current.device,
        [part]: value,
      };
      if (targetField === "purchase_date") {
        device.purchase_date_sn_message = "";
      } else if (targetField === "imin_expiry_date") {
        device.imin_warranty_sn_message = "";
        device.imin_warranty_lookup_result = "";
      }
      const prefix = targetField === "purchase_date" ? "purchase_date" : targetField.replace("_date", "");
      const day = device[`${prefix}_day`];
      const month = device[`${prefix}_month`];
      const year = device[`${prefix}_year`];
      return {
        ...current,
        device: {
          ...device,
          [targetField]: day && month && year ? `${year}-${month}-${day}` : "",
        },
      };
    });
    setCreateErrors((current) => {
      const key = `device.${targetField}`;
      if (!current[key]) return current;
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
    setCreateNotice("");
  };

  const toggleWarrantyDurationLock = () => {
    updateDraftField(["device", "dvt_duration_locked"], !createDraft.device.dvt_duration_locked);
  };

  const toggleAccessory = (accessory) => {
    setCreateDraft((current) => {
      const selected = current.accessories.items.includes(accessory);
      const items = selected
        ? current.accessories.items.filter((item) => item !== accessory)
        : [...current.accessories.items, accessory];
      return {
        ...current,
        accessories: {
          ...current.accessories,
          items,
          other_details: items.includes("other") ? current.accessories.other_details : "",
        },
      };
    });
    setCreateErrors((current) => {
      const next = { ...current };
      delete next["accessories.items"];
      if (accessory === "other") delete next["accessories.other_details"];
      return next;
    });
    setCreateNotice("");
  };

  const validateCreateStep = (step) => {
    const errors = {};
    if (step === 0) {
      if (!createDraft.category) errors.category = "Category is required.";
      if (!createDraft.branch) errors.branch = "Branch is required.";
    }
    if (step === 1) {
      const currentSerial = createDraft.device.serial_number.trim();
      if (!currentSerial) {
        errors["device.serial_number"] = "Serial Number is required.";
      } else if (
        !createDraft.device.sn_check_result ||
        createDraft.device.sn_check_serial_number !== currentSerial
      ) {
        errors["device.sn_check"] = "Generate SN Check before creating this ticket.";
      }
      if (!createDraft.device.purchase_date) {
        errors["device.purchase_date"] = "Purchase Date is required.";
      } else if (!isValidISODateOnly(createDraft.device.purchase_date)) {
        errors["device.purchase_date"] = `Purchase Date must be a valid date from 2020 through ${CURRENT_YEAR}.`;
      }
      if (!createDraft.device.model_selection.trim()) {
        errors["device.model_selection"] = "Model Selection is required.";
      }
      const durationYears = Number.parseInt(createDraft.device.dvt_duration_years, 10);
      if (!Number.isInteger(durationYears) || durationYears < 1) {
        errors["device.dvt_duration_years"] = "DVT Warranty Duration must be at least 1 year.";
      }
      if (
        ["unknown", "unavailable"].includes(createDraft.device.sn_check_result) &&
        !createDraft.unknown_stock_reference_confirmed
      ) {
        errors.unknown_stock_reference_confirmed = "Please confirm/tick.";
      }
      const iminPartsSelected = [
        createDraft.device.imin_expiry_day,
        createDraft.device.imin_expiry_month,
        createDraft.device.imin_expiry_year,
      ].some(Boolean);
      if (
        iminPartsSelected &&
        !isValidISODateOnly(createDraft.device.imin_expiry_date, {
          maxYear: CURRENT_YEAR + 3,
        })
      ) {
        errors["device.imin_expiry_date"] = "Enter a complete valid iMin Warranty Exp Date.";
      }
    }
    if (step === 2) {
      if (!createDraft.owner.name.trim()) errors["owner.name"] = "Company / Store Name is required.";
      if (!createDraft.owner.contact.trim()) errors["owner.contact"] = "Contact is required.";
    }
    if (step === 3) {
      if (
        createDraft.accessories.items.includes("other") &&
        !createDraft.accessories.other_details.trim()
      ) {
        errors["accessories.other_details"] = "Other Accessory Details is required.";
      }
    }
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAllCreateSteps = () => {
    const errors = {};
    if (!createDraft.category) errors.category = "Category is required.";
    if (!createDraft.branch) errors.branch = "Branch is required.";
    if (!createDraft.owner.name.trim()) errors["owner.name"] = "Company / Store Name is required.";
    if (!createDraft.owner.contact.trim()) errors["owner.contact"] = "Contact is required.";
    if (!createDraft.device.serial_number.trim()) {
      errors["device.serial_number"] = "Serial Number is required.";
    }
    if (!createDraft.device.purchase_date) {
      errors["device.purchase_date"] = "Purchase Date is required.";
    } else if (!isValidISODateOnly(createDraft.device.purchase_date)) {
      errors["device.purchase_date"] = `Purchase Date must be a valid date from 2020 through ${CURRENT_YEAR}.`;
    }
    if (!createDraft.device.model_selection.trim()) {
      errors["device.model_selection"] = "Model Selection is required.";
    }
    const durationYears = Number.parseInt(createDraft.device.dvt_duration_years, 10);
    if (!Number.isInteger(durationYears) || durationYears < 1) {
      errors["device.dvt_duration_years"] = "DVT Warranty Duration must be at least 1 year.";
    }
    const iminPartsSelected = [
      createDraft.device.imin_expiry_day,
      createDraft.device.imin_expiry_month,
      createDraft.device.imin_expiry_year,
    ].some(Boolean);
    if (
      iminPartsSelected &&
      !isValidISODateOnly(createDraft.device.imin_expiry_date, {
        maxYear: CURRENT_YEAR + 3,
      })
    ) {
      errors["device.imin_expiry_date"] = "Enter a complete valid iMin Warranty Exp Date.";
    }
    if (
      createDraft.accessories.items.includes("other") &&
      !createDraft.accessories.other_details.trim()
    ) {
      errors["accessories.other_details"] = "Other Accessory Details is required.";
    }

    const dateIn = new Date();
    const purchaseDate = parseISODateOnly(createDraft.device.purchase_date);
    const dateInDateOnly = new Date(dateIn.getFullYear(), dateIn.getMonth(), dateIn.getDate());
    if (purchaseDate && dateInDateOnly < purchaseDate) {
      errors["device.purchase_date"] = "Purchase Date cannot be after the ticket creation date.";
    }

    const checkedSerial = createDraft.device.sn_check_serial_number;
    const currentSerial = createDraft.device.serial_number.trim();
    if (!createDraft.device.sn_check_result || checkedSerial !== currentSerial) {
      errors["device.sn_check"] = "Generate SN Check before creating this ticket.";
    }
    if (
      ["unknown", "unavailable"].includes(createDraft.device.sn_check_result) &&
      !createDraft.unknown_stock_reference_confirmed
    ) {
      errors.unknown_stock_reference_confirmed = "Please confirm/tick.";
    }

    setCreateErrors(errors);
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      if (errorKeys.some((key) => ["category", "branch"].includes(key))) {
        setCreateStep(0);
      } else if (errorKeys.some((key) => key.startsWith("owner."))) {
        setCreateStep(1);
      } else if (errorKeys.some((key) => key.startsWith("device.") || key === "unknown_stock_reference_confirmed")) {
        setCreateStep(2);
      } else {
        setCreateStep(3);
      }
    }
    return errorKeys.length === 0;
  };

  const handleGenerateSnCheck = async () => {
    const errors = {};
    const requestedSerialNumber = createDraft.device.serial_number.trim();
    if (!requestedSerialNumber) {
      errors["device.serial_number"] = "Serial Number is required.";
    }
    if (Object.keys(errors).length > 0) {
      setCreateErrors((current) => ({ ...current, ...errors }));
      return;
    }

    const requestId = snCheckRequestIdRef.current + 1;
    snCheckRequestIdRef.current = requestId;
    setSnCheckLoading(true);
    setCreateDraft((current) => ({
      ...current,
      device: {
        ...current.device,
        purchase_date_sn_message: "",
        imin_warranty_sn_message: "",
        imin_warranty_lookup_result: "",
      },
    }));
    setCreateErrors((current) => {
      const next = { ...current };
      delete next["device.sn_check"];
      delete next["device.sn_check_api"];
      delete next.unknown_stock_reference_confirmed;
      return next;
    });
    setCreateNotice("");

    try {
      const response = await checkServiceSerialNumber({
        serial_number: requestedSerialNumber,
      });
      if (snCheckRequestIdRef.current !== requestId) return;
      const result = response?.data || {};
      const snResult = result.result;
      const stockReferenceStatus = result.stock_reference_status || "unknown";
      const apiModel = result.model || "";
      const warning = result.warning || response?.message || "";
      const suggestedPurchaseDate = result.suggested_purchase_date || "";
      const iminWarranty = result.imin_warranty;

      setCreateDraft((current) => {
        const next = {
          ...current,
          device: {
            ...current.device,
            stock_reference_status: stockReferenceStatus,
            sn_check_result: snResult,
            sn_check_serial_number: requestedSerialNumber,
            sn_check_warning: warning,
          },
          unknown_stock_reference_confirmed: false,
        };

        if (snResult === "known") {
          next.device.model_selection = "other";
          next.device.model = apiModel;
          next.device.stock_reference_status = "known";
          if (suggestedPurchaseDate) {
            const purchaseDateParts = splitISODateOnly(suggestedPurchaseDate);
            next.device.purchase_date = suggestedPurchaseDate;
            next.device.purchase_date_day = purchaseDateParts.day;
            next.device.purchase_date_month = purchaseDateParts.month;
            next.device.purchase_date_year = purchaseDateParts.year;
            next.device.purchase_date_sn_message = "Purchase Date updated from SN Check.";
          } else {
            next.device.purchase_date_sn_message = "Purchase Date is not available from SN Check.";
          }
        } else {
          next.device.stock_reference_status = "unknown";
          next.device.model = next.device.model_selection
            ? modelNameForSelection(deviceModelOptions, next.device.model_selection)
            : "";
          next.device.purchase_date_sn_message = "";
        }

        if (iminWarranty?.lookup_result === "found") {
          const expiryDate = iminWarranty.expiry_date || "";
          const expiryParts = splitISODateOnly(expiryDate);
          if (
            expiryDate &&
            ["valid", "invalid"].includes(iminWarranty.status) &&
            expiryParts.day && expiryParts.month && expiryParts.year
          ) {
            next.device.imin_expiry_date = expiryDate;
            next.device.imin_expiry_day = expiryParts.day;
            next.device.imin_expiry_month = expiryParts.month;
            next.device.imin_expiry_year = expiryParts.year;
            next.device.imin_status = iminWarranty.status;
            next.device.imin_warranty_lookup_result = "found";
            next.device.imin_warranty_sn_message = "iMin warranty fields updated from Generate.";
          }
        } else if (iminWarranty?.lookup_result === "not_found") {
          next.device.imin_warranty_lookup_result = "not_found";
          next.device.imin_warranty_sn_message = "iMin warranty data was not found.";
        } else if (iminWarranty?.lookup_result === "unavailable") {
          next.device.imin_warranty_lookup_result = "unavailable";
          next.device.imin_warranty_sn_message = "iMin warranty lookup is unavailable.";
        }

        return next;
      });
      setCreateErrors((current) => {
        const next = { ...current };
        if (
          snResult === "known" &&
          suggestedPurchaseDate &&
          isValidISODateOnly(suggestedPurchaseDate)
        ) {
          delete next["device.purchase_date"];
        }
        if (snResult === "known") {
          delete next["device.model_selection"];
        }
        if (
          iminWarranty?.lookup_result === "found" &&
          ["valid", "invalid"].includes(iminWarranty.status) &&
          isValidISODateOnly(iminWarranty.expiry_date || "", {
            maxYear: CURRENT_YEAR + 3,
          })
        ) {
          delete next["device.imin_expiry_date"];
        }
        return next;
      });
    } catch (err) {
      if (snCheckRequestIdRef.current !== requestId) return;
      setCreateErrors((current) => ({
        ...current,
        "device.sn_check_api": formatApiError(err),
      }));
      setCreateDraft((current) => ({
        ...current,
        device: { ...current.device, purchase_date_sn_message: "" },
      }));
    } finally {
      if (snCheckRequestIdRef.current === requestId) {
        setSnCheckLoading(false);
      }
    }
  };

  const buildCreateTicketPayload = () => {
    const dateIn = new Date().toISOString();
    return {
      category: createDraft.category,
      branch: createDraft.branch,
      date_in: dateIn,
      owner: {
        name: createDraft.owner.name.trim(),
        pic: createDraft.owner.pic.trim(),
        contact: createDraft.owner.contact.trim(),
        address: createDraft.owner.address.trim(),
      },
      device: {
        serial_number: createDraft.device.serial_number.trim(),
        purchase_date: createDraft.device.purchase_date,
        model_selection: createDraft.device.model_selection,
      },
      unknown_stock_reference_confirmed:
        createDraft.device.stock_reference_status === "unknown" &&
        createDraft.unknown_stock_reference_confirmed,
      warranty: {
        dvt: {
          duration_years: Number.parseInt(createDraft.device.dvt_duration_years, 10),
        },
        imin: {
          status: createDraft.device.imin_status,
          expiry_date: createDraft.device.imin_expiry_date || null,
        },
      },
      accessories: {
        items: createDraft.accessories.items,
        other_details: createDraft.accessories.items.includes("other")
          ? createDraft.accessories.other_details.trim()
          : "",
      },
      notes: createDraft.notes.trim(),
    };
  };

  const handleCreateTicket = async () => {
    if (createSubmitting) return;
    if (!validateAllCreateSteps()) return;

    setCreateSubmitting(true);
    setCreateNotice("");
    try {
      const response = await createServiceTicket(buildCreateTicketPayload());
      const ticketNumber = response?.data?.ticket_number || response?.ticket_number;
      closeCreateModal();
      toast.success(
        ticketNumber
          ? `Service ticket ${ticketNumber} created successfully.`
          : "Service ticket created successfully."
      );
      setListRefreshKey((current) => current + 1);
    } catch (err) {
      const serviceError = getServiceErrorPayload(err);
      const apiFieldErrors = serviceError?.field_errors;
      const mappedErrors =
        apiFieldErrors && typeof apiFieldErrors === "object" && !Array.isArray(apiFieldErrors)
          ? Object.fromEntries(
              Object.entries(apiFieldErrors).map(([field, message]) => [
                field,
                typeof message === "string" ? message : "Review this field.",
              ])
            )
          : {};
      if (serviceError?.code === "SERVICE_DATE_IN_BEFORE_PURCHASE") {
        mappedErrors["device.purchase_date"] =
          mappedErrors["device.purchase_date"] || "Purchase Date cannot be after the ticket creation date.";
      }
      if (serviceError?.code === "SERVICE_UNKNOWN_SN_CONFIRMATION_REQUIRED") {
        mappedErrors.unknown_stock_reference_confirmed =
          mappedErrors.unknown_stock_reference_confirmed ||
          "Please confirm/tick.";
      }
      if (Object.keys(mappedErrors).length > 0) {
        setCreateErrors(mappedErrors);
        const errorKeys = Object.keys(mappedErrors);
        if (errorKeys.some((key) => ["category", "branch"].includes(key))) setCreateStep(0);
        else if (errorKeys.some((key) => key.startsWith("owner."))) setCreateStep(2);
        else if (
          errorKeys.some(
            (key) => key.startsWith("device.") || key === "unknown_stock_reference_confirmed"
          )
        ) setCreateStep(1);
        else setCreateStep(3);
      }
      setCreateNotice(Object.keys(mappedErrors).length > 0 ? "Review the highlighted fields." : formatApiError(err));
      setCreateSubmitting(false);
    }
  };

  const goToNextCreateStep = () => {
    if (!validateCreateStep(createStep)) {
      if (
        createStep === 1 &&
        ["unknown", "unavailable"].includes(createDraft.device.sn_check_result) &&
        !createDraft.unknown_stock_reference_confirmed
      ) {
        stockReferenceConfirmationRef.current?.focus();
      }
      return;
    }
    setCreateStep((current) => Math.min(CREATE_STEPS.length - 1, current + 1));
    setCreateNotice("");
  };

  const goToPreviousCreateStep = () => {
    setCreateStep((current) => Math.max(0, current - 1));
    setCreateErrors({});
    setCreateNotice("");
  };

  const reservedErrorText = (key) => (
    <p className="mt-1 min-h-[18px] text-xs text-destructive">
      {createErrors[key] || ""}
    </p>
  );

  const segmentButtonClass = (active, index) =>
    `flex min-h-[48px] min-w-0 items-center justify-center gap-2 border-l px-4 py-3 text-sm font-semibold transition-colors first:border-l-0 focus-visible:relative focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary ${
      active
        ? "border-primary bg-primary text-primary-foreground"
        : "border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
    } ${index === 0 ? "border-l-0" : ""}`;

  const inputClass =
    "field-editable w-full rounded-sm border px-3 py-2.5 text-sm outline-none transition-colors";
  const readOnlyInputClass =
    "field-readonly w-full rounded-sm border px-3 py-2.5 text-sm outline-none";
  const labelClass = "text-xs font-semibold text-zinc-700";
  const dvtPreview = calculateDvtPreview(
    createDraft.device.purchase_date,
    createDraft.device.dvt_duration_years
  );
  const dvtPreviewParts = splitISODateOnly(dvtPreview.expiry_date);
  const snCheckResultMessage =
    createDraft.device.sn_check_result === "known"
      ? "SN found"
      : ["unknown", "unavailable"].includes(createDraft.device.sn_check_result)
        ? "SN not found, please select a model and tick the stock reference"
        : "";
  const snCheckError =
    createErrors["device.sn_check_api"] || createErrors["device.sn_check"] || "";
  const serialNumberMessage =
    createErrors["device.serial_number"] || snCheckError || snCheckResultMessage;
  const serialNumberMessageClass =
    createErrors["device.serial_number"] || snCheckError
      ? "text-destructive"
      : createDraft.device.sn_check_result === "known"
        ? "text-success"
        : "text-warning";
  const deviceRowTwoLabelClass = "flex h-4 items-center";
  const deviceRowTwoControlClass = "mt-2 h-[42px]";

  const renderSplitDateSelects = ({
    dayKey,
    monthKey,
    yearKey,
    targetField,
    disabled = false,
    labelPrefix,
    valueOverride = null,
    yearOptions = PURCHASE_YEAR_OPTIONS,
  }) => (
    <div className="mt-2 grid grid-cols-[0.7fr_1.2fr_0.9fr] gap-2">
      <select
        value={valueOverride?.day ?? createDraft.device[dayKey]}
        onChange={(event) => updateDeviceDatePart(targetField, dayKey, event.target.value)}
        disabled={disabled}
        className={disabled ? readOnlyInputClass : inputClass}
        aria-label={`${labelPrefix} day`}
      >
        <option value="">Day</option>
        {PURCHASE_DAY_OPTIONS.map((day) => (
          <option key={day} value={day}>
            {day}
          </option>
        ))}
      </select>
      <select
        value={valueOverride?.month ?? createDraft.device[monthKey]}
        onChange={(event) => updateDeviceDatePart(targetField, monthKey, event.target.value)}
        disabled={disabled}
        className={disabled ? readOnlyInputClass : inputClass}
        aria-label={`${labelPrefix} month`}
      >
        <option value="">Month</option>
        {PURCHASE_MONTH_OPTIONS.map((month) => (
          <option key={month.value} value={month.value}>
            {month.label}
          </option>
        ))}
      </select>
      <select
        value={valueOverride?.year ?? createDraft.device[yearKey]}
        onChange={(event) => updateDeviceDatePart(targetField, yearKey, event.target.value)}
        disabled={disabled}
        className={disabled ? readOnlyInputClass : inputClass}
        aria-label={`${labelPrefix} year`}
      >
        <option value="">Year</option>
        {yearOptions.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );

  const renderStepCategory = () => (
    <div className="space-y-6">
      <div>
        <div id="service-create-category-label" className={labelClass}>Category</div>
        <div
          className="mt-2 grid grid-cols-3 overflow-hidden rounded-sm border border-border bg-muted"
          role="group"
          aria-labelledby="service-create-category-label"
          data-testid="service-create-category-segments"
        >
          {Object.values(SERVICE_CATEGORIES).map((option, index) => (
            <button
              key={option}
              type="button"
              onClick={() => updateDraftField(["category"], option)}
              className={segmentButtonClass(createDraft.category === option, index)}
              aria-pressed={createDraft.category === option}
              data-value={option}
            >
              <span
                aria-hidden="true"
                className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  createDraft.category === option
                    ? "border-primary-foreground/70"
                    : "border-muted-foreground"
                }`}
              >
                {createDraft.category === option ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
              </span>
              <span>{SERVICE_CATEGORY_LABELS[option]}</span>
            </button>
          ))}
        </div>
        {reservedErrorText("category")}
      </div>

      <div>
        <div id="service-create-branch-label" className={labelClass}>Branch</div>
        <div
          className="mt-2 grid grid-cols-3 overflow-hidden rounded-sm border border-border bg-muted"
          role="group"
          aria-labelledby="service-create-branch-label"
          data-testid="service-create-branch-segments"
        >
          {SERVICE_LIST_BRANCHES.map((branch, index) => (
            <button
              key={branch}
              type="button"
              onClick={() => updateDraftField(["branch"], branch)}
              className={segmentButtonClass(createDraft.branch === branch, index)}
              aria-pressed={createDraft.branch === branch}
              data-value={branch}
            >
              <span
                aria-hidden="true"
                className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  createDraft.branch === branch
                    ? "border-primary-foreground/70"
                    : "border-muted-foreground"
                }`}
              >
                {createDraft.branch === branch ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
              </span>
              <span>{SERVICE_BRANCH_LABELS[branch]}</span>
            </button>
          ))}
        </div>
        {reservedErrorText("branch")}
      </div>

    </div>
  );

  const renderStepOwner = () => (
    <div className="grid gap-5 md:grid-cols-2">
      <div className="md:col-span-2">
        <label className={labelClass} htmlFor="service-create-owner-name">
          Company / Store Name
        </label>
        <input
          id="service-create-owner-name"
          value={createDraft.owner.name}
          onChange={(event) => updateDraftField(["owner", "name"], event.target.value)}
          placeholder="Enter company or store name"
          className={`${inputClass} mt-2`}
        />
        {reservedErrorText("owner.name")}
      </div>
      <div>
        <label className={labelClass} htmlFor="service-create-owner-pic">
          PIC
        </label>
        <input
          id="service-create-owner-pic"
          value={createDraft.owner.pic}
          onChange={(event) => updateDraftField(["owner", "pic"], event.target.value)}
          placeholder="Enter PIC name"
          className={`${inputClass} mt-2`}
        />
        {reservedErrorText("owner.pic")}
      </div>
      <div>
        <label className={labelClass} htmlFor="service-create-owner-contact">
          Contact
        </label>
        <input
          id="service-create-owner-contact"
          type="text"
          value={createDraft.owner.contact}
          onChange={(event) => updateDraftField(["owner", "contact"], event.target.value)}
          placeholder="Enter phone number or contact"
          className={`${inputClass} mt-2`}
        />
        {reservedErrorText("owner.contact")}
      </div>
      <div className="md:col-span-2">
        <label className={labelClass} htmlFor="service-create-owner-address">
          Address
        </label>
        <textarea
          id="service-create-owner-address"
          value={createDraft.owner.address}
          onChange={(event) => updateDraftField(["owner", "address"], event.target.value)}
          placeholder="Enter customer address"
          className={`${inputClass} mt-2 min-h-[96px] resize-none`}
        />
        {reservedErrorText("owner.address")}
      </div>
    </div>
  );

  const renderStepDevice = () => (
    <div className="space-y-5" data-testid="service-create-device-layout">
      <div
        className="grid gap-5 lg:grid-cols-2"
        data-testid="service-create-device-row-1"
        data-device-fields="serial_number,generate|model_selection,model"
      >
        <div
          className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
          data-testid="service-create-device-row-1-left"
        >
          <div>
            <label className={labelClass} htmlFor="service-create-serial-number">Serial Number</label>
            <input
              id="service-create-serial-number"
              value={createDraft.device.serial_number}
              onChange={(event) => updateDraftField(["device", "serial_number"], event.target.value)}
              className={`${inputClass} mt-2 font-semibold tracking-[0.02em]`}
              placeholder="Enter serial number"
            />
            <p
              className={`mt-1 min-h-[18px] text-xs ${serialNumberMessageClass}`}
              data-testid="service-create-serial-message"
            >
              {serialNumberMessage}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleGenerateSnCheck}
            loading={snCheckLoading}
            loadingLabel="Generating..."
            title="Generate SN Check"
            className="mt-[26px]"
            data-testid="service-create-generate"
          >
            Generate
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="service-create-device-row-1-right">
          <div>
            <div className={deviceRowTwoLabelClass} data-device-row-two-label>
              <label className={labelClass} htmlFor="service-create-model-selection">Model Selection</label>
            </div>
            <select
              id="service-create-model-selection"
              value={createDraft.device.model_selection}
              onChange={(event) => updateDraftField(["device", "model_selection"], event.target.value)}
              disabled={createDraft.device.sn_check_result === "known"}
              className={`${createDraft.device.sn_check_result === "known" ? readOnlyInputClass : inputClass} ${deviceRowTwoControlClass}`}
            >
              <option value="">{deviceModelsLoading ? "Loading models..." : "Select model"}</option>
              {deviceModelOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <p className="mt-1 min-h-[18px] text-xs text-destructive">
              {createErrors["device.model_selection"] || deviceModelsError}
            </p>
          </div>
          <div>
            <div className={deviceRowTwoLabelClass} data-device-row-two-label>
              <label className={labelClass} htmlFor="service-create-model">Model</label>
            </div>
            <input
              id="service-create-model"
              value={createDraft.device.model || "Auto-filled after selection"}
              readOnly
              className={`${readOnlyInputClass} ${deviceRowTwoControlClass}`}
            />
            {reservedErrorText("device.model")}
          </div>
        </div>
      </div>

      <div
        className="grid gap-5 lg:grid-cols-2"
        data-testid="service-create-device-row-2"
        data-device-fields="purchase_date|stock_reference_status,dvt_duration_years"
      >
        <div>
          <label className={labelClass} htmlFor="service-create-purchase-date">Purchase Date</label>
          <div id="service-create-purchase-date">
            {renderSplitDateSelects({
              dayKey: "purchase_date_day",
              monthKey: "purchase_date_month",
              yearKey: "purchase_date_year",
              targetField: "purchase_date",
              labelPrefix: "Purchase",
              yearOptions: PURCHASE_YEAR_OPTIONS,
            })}
          </div>
          <p
            className={`mt-1 min-h-[18px] text-xs ${
              createErrors["device.purchase_date"]
                ? "text-destructive"
                : createDraft.device.purchase_date_sn_message === "Purchase Date updated from SN Check."
                  ? "text-success"
                  : "text-warning"
            }`}
            data-testid="service-create-purchase-date-message"
          >
            {createErrors["device.purchase_date"] || createDraft.device.purchase_date_sn_message || ""}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="service-create-device-row-2-right">
          <div>
            <div
              className={deviceRowTwoLabelClass}
              data-testid="service-create-stock-reference-label-row"
              data-device-row-two-label
            >
              <label
                id="service-create-stock-reference-label"
                className={labelClass}
              >
                Stock Reference Status
              </label>
            </div>
            <div
              id="service-create-stock-reference"
              role="group"
              aria-labelledby="service-create-stock-reference-label"
              data-field-state="readonly"
              data-testid="service-create-stock-reference-field"
              className={`field-readonly flex w-full items-center justify-between gap-3 rounded-sm border px-3 text-sm outline-none ${deviceRowTwoControlClass}`}
            >
              <span>
                {createDraft.device.stock_reference_status
                  ? createDraft.device.stock_reference_status === "known" ? "Known" : "Unknown"
                  : "Not generated"}
              </span>
              {createDraft.device.stock_reference_status === "unknown" ? (
                <input
                  type="checkbox"
                  ref={stockReferenceConfirmationRef}
                  checked={createDraft.unknown_stock_reference_confirmed}
                  onChange={(event) => updateDraftField(["unknown_stock_reference_confirmed"], event.target.checked)}
                  required
                  aria-label="Confirm unknown stock reference"
                  className="h-4 w-4 shrink-0 accent-primary"
                  data-testid="service-create-stock-reference-confirmation"
                />
              ) : null}
            </div>
            {reservedErrorText("unknown_stock_reference_confirmed")}
          </div>
          <div>
            <div className={deviceRowTwoLabelClass} data-device-row-two-label>
              <label className={labelClass} htmlFor="service-create-dvt-duration">DVT Warranty Duration</label>
            </div>
            <div className={`${deviceRowTwoControlClass} flex overflow-hidden rounded-sm border ${createDraft.device.dvt_duration_locked ? "field-readonly" : "field-editable"}`}>
              <input
                id="service-create-dvt-duration"
                type="number"
                min="1"
                step="1"
                value={createDraft.device.dvt_duration_years}
                onChange={(event) => updateDraftField(["device", "dvt_duration_years"], event.target.value)}
                disabled={createDraft.device.dvt_duration_locked}
                className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm outline-none"
              />
              <button
                type="button"
                onClick={toggleWarrantyDurationLock}
                className={`flex w-11 shrink-0 items-center justify-center border-l transition-colors ${
                  createDraft.device.dvt_duration_locked
                    ? "border-warning/40 bg-warning/15 text-warning hover:bg-warning/20"
                    : "border-primary/40 bg-primary/15 text-primary hover:bg-primary/20"
                }`}
                aria-pressed={!createDraft.device.dvt_duration_locked}
                aria-label={createDraft.device.dvt_duration_locked ? "Unlock warranty duration" : "Lock warranty duration"}
              >
                {createDraft.device.dvt_duration_locked ? <Lock className="h-4 w-4" strokeWidth={2} /> : <Unlock className="h-4 w-4" strokeWidth={2} />}
              </button>
            </div>
            {reservedErrorText("device.dvt_duration_years")}
          </div>
        </div>
      </div>

      <div
        className="grid gap-5 lg:grid-cols-2"
        data-testid="service-create-device-row-3"
        data-device-fields="dvt_expiry_date,dvt_status"
      >
        <div>
          <label className={labelClass} htmlFor="service-create-dvt-expiry">DVT Warranty Exp Date</label>
          <div id="service-create-dvt-expiry">
            {renderSplitDateSelects({
              dayKey: "dvt_expiry_day",
              monthKey: "dvt_expiry_month",
              yearKey: "dvt_expiry_year",
              targetField: "dvt_expiry_date",
              disabled: true,
              labelPrefix: "DVT warranty expiry",
              valueOverride: dvtPreviewParts,
              yearOptions: WARRANTY_YEAR_OPTIONS,
            })}
          </div>
          {reservedErrorText("device.dvt_expiry_date")}
        </div>
        <div>
          <label className={labelClass} htmlFor="service-create-dvt-status">DVT Warranty Status</label>
          <input
            id="service-create-dvt-status"
            value={dvtPreview.status || "Generated after Purchase Date"}
            readOnly
            className={`${readOnlyInputClass} mt-2`}
          />
          {reservedErrorText("device.dvt_status")}
        </div>
      </div>

      <div
        className="grid gap-5 lg:grid-cols-2"
        data-testid="service-create-device-row-4"
        data-device-fields="imin_expiry_date,imin_status"
      >
        <div>
          <label className={labelClass} htmlFor="service-create-imin-expiry">iMin Warranty Exp Date</label>
          <div id="service-create-imin-expiry">
            {renderSplitDateSelects({
              dayKey: "imin_expiry_day",
              monthKey: "imin_expiry_month",
              yearKey: "imin_expiry_year",
              targetField: "imin_expiry_date",
              labelPrefix: "iMin warranty expiry",
              yearOptions: WARRANTY_YEAR_OPTIONS,
            })}
          </div>
          <p
            className={`mt-1 min-h-[18px] text-xs ${
              createDraft.device.imin_warranty_lookup_result === "found"
                ? "text-success"
                : "text-warning"
            }`}
            data-testid="service-create-imin-warranty-message"
          >
            {createDraft.device.imin_warranty_sn_message}
          </p>
          {reservedErrorText("device.imin_expiry_date")}
        </div>
        <div>
          <label className={labelClass} htmlFor="service-create-imin-status">iMin Warranty Status</label>
          <select
            id="service-create-imin-status"
            value={createDraft.device.imin_status}
            onChange={(event) => updateDraftField(["device", "imin_status"], event.target.value)}
            className={`${inputClass} mt-2`}
          >
            {IMIN_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {reservedErrorText("device.imin_status")}
        </div>
      </div>
    </div>
  );

  const renderStepAccessories = () => (
    <div className="space-y-5">
      <div>
        <div className={labelClass}>Accessories</div>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {ACCESSORY_OPTIONS.map((option) => {
            const checked = createDraft.accessories.items.includes(option.value);
            return (
              <label
                key={option.value}
                className={`flex cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-sm transition-colors ${
                  checked
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-zinc-200 bg-white text-zinc-600 hover:border-primary"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleAccessory(option.value)}
                  className="h-4 w-4 accent-primary"
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
        {reservedErrorText("accessories.items")}
      </div>

      <div>
        <label className={labelClass} htmlFor="service-create-other-accessory">
          Other Accessory Details
        </label>
        <input
          id="service-create-other-accessory"
          type="text"
          value={createDraft.accessories.other_details}
          onChange={(event) => updateDraftField(["accessories", "other_details"], event.target.value)}
          disabled={!createDraft.accessories.items.includes("other")}
          placeholder="Describe other accessory"
          className={`mt-2 w-full rounded-sm border px-3 py-2.5 text-sm outline-none transition-colors ${
            createDraft.accessories.items.includes("other")
              ? "border-zinc-300 bg-white text-zinc-900 focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-zinc-500 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:border-primary"
              : "cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-400 opacity-70 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-600"
          }`}
        />
        {reservedErrorText("accessories.other_details")}
      </div>

      <div>
        <label className={labelClass} htmlFor="service-create-notes">
          Notes
        </label>
        <textarea
          id="service-create-notes"
          value={createDraft.notes}
          onChange={(event) => updateDraftField(["notes"], event.target.value)}
          className={`${inputClass} mt-2 min-h-[110px] resize-none`}
        />
      </div>
    </div>
  );

  const renderCreateStep = () => {
    if (createStep === 0) return renderStepCategory();
    if (createStep === 1) return renderStepDevice();
    if (createStep === 2) return renderStepOwner();
    return renderStepAccessories();
  };

  const stageSelector = (
    <div
      className="grid w-full grid-cols-6 gap-[5px]"
      data-testid="service-stage-tabs"
      data-stage-gap={`${STAGE_VISIBLE_GAP_PX}px`}
      data-stage-chevron-depth={`${STAGE_CHEVRON_DEPTH_PX}px`}
      data-stage-effective-gap={`${STAGE_VISIBLE_GAP_PX}px`}
      data-stage-layout="equal-six-columns"
    >
      {STAGE_TABS.map((stage, index) => (
        <button
          key={stage}
          type="button"
          onClick={() => changeStage(stage)}
          aria-pressed={selectedStage === stage}
          className="relative flex min-h-[48px] w-full min-w-0 items-center gap-2 text-left text-xs font-semibold transition-colors hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
          style={stageSegmentStyle(stage, index)}
          data-testid={`service-stage-${stage}`}
        >
          <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold"
            style={stageNumberStyle(stage)}
          >
            {index + 1}
          </span>
          <span className="whitespace-nowrap">
            {SERVICE_STAGE_LABELS[stage]}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <OrdinaryPageBody data-testid={`service-tickets-${category}`}>
      <OrdinaryPageHeader
        title={categoryLabel}
        actions={<>
          {canViewDeletedServiceTickets(user?.role) ? (
            <Button
              type="button"
              onClick={() => navigate(deletedTicketsRoute())}
              variant="transfer-remove"
              data-action-category="destructive"
              data-testid="service-deleted-tickets-btn"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
              Deleted Tickets
            </Button>
          ) : null}
          {["admin", "staff"].includes(user?.role) ? (
            <Button
              type="button"
              onClick={() => setExportModalOpen(true)}
              variant="export"
              data-action-category="export-download"
              data-testid="service-export-btn"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={2} />
              Export
            </Button>
          ) : null}
          {["admin", "staff"].includes(user?.role) ? (
            <Button
              type="button"
              onClick={openCreateModal}
              data-action-category="create-add"
              data-testid="service-add-new-btn"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2} />
              Add New
            </Button>
          ) : null}
        </>}
        data-testid="service-list-header"
      />

      <ListTableShell data-testid="service-list-panel">

        <ServiceBranchSearchRow
          selectedBranch={selectedBranch}
          onBranchChange={changeBranch}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchControl={isKnownCategory ? (
            <div className="flex items-end gap-2" data-testid={`service-${category}-search-date-controls`}>
              <div className="w-[170px]" data-testid={`service-${category}-date-range`}>
                <DateRangeSelector
                  value={soldDateRange}
                  onChange={setSoldDateRange}
                  onApply={applySoldDateRange}
                  mode="immediate"
                  label={null}
                  aria-label="Service Date In Range"
                  controlsClassName="h-9"
                  id={`service-${category}-date-range`}
                />
              </div>
              <GlobalSearch
                value={searchInput}
                onValueChange={setSearchInput}
                onApply={applySoldGlobalSearch}
                placeholder="Global Search"
                inputProps={{
                  "aria-label": "Global Search",
                  "data-testid": "service-search-input",
                }}
              />
            </div>
          ) : null}
        />

        {stageSelector}

        {listSuccessMessage ? (
          <div className="px-6 py-4 text-sm text-primary" data-testid="service-ticket-list-success">
            {listSuccessMessage}
          </div>
        ) : null}

        {error && tickets.length > 0 ? (
          <ListErrorNotice
            onRetry={() => setListRefreshKey((value) => value + 1)}
            testId="service-ticket-list-error"
          >
            {error}
          </ListErrorNotice>
        ) : null}

        <ListTableViewport>
          <ListTable
            geometry={SOLD_TABLE_GEOMETRY}
            data-testid="service-ticket-table"
          >
            <thead>
              <tr>
                <th style={{ width: "70px" }}>No.</th>
                <th style={{ width: "150px" }}>Date In</th>
                <th>Customer Name</th>
                <th style={{ width: "190px" }}>Serial Number</th>
                <th>Model</th>
                <th style={{ width: "190px" }}>Service Cost</th>
                <th style={{ width: "140px" }}>DVT Warranty</th>
                <th style={{ width: "170px" }}>Actions</th>
              </tr>
              {isKnownCategory ? (
                <ColumnFilterRow className="bg-white" data-testid={`service-${category}-filter-row`}>
                  <ColumnFilterCell aria-label="No filter" />
                  <ColumnFilterCell>
                    <ColumnDateFilter
                      value={soldFilterDrafts.date_in}
                      onValueChange={(value) => updateSoldFilterDraft("date_in", value)}
                      onApply={(value) => applySoldFilter("date_in", value)}
                      aria-label="Filter Date In"
                      data-testid={`service-${category}-filter-date-in`}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell>
                    <ColumnTextFilter
                      value={soldFilterDrafts.owner_name}
                      onValueChange={(value) => updateSoldFilterDraft("owner_name", value)}
                      onApply={(value) => applySoldFilter("owner_name", value)}
                      placeholder="Filter…"
                      aria-label="Filter Customer Name"
                      data-testid={`service-${category}-filter-owner-name`}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell>
                    <ColumnTextFilter
                      value={soldFilterDrafts.serial_number}
                      onValueChange={(value) => updateSoldFilterDraft("serial_number", value)}
                      onApply={(value) => applySoldFilter("serial_number", value)}
                      placeholder="Filter…"
                      aria-label="Filter Serial Number"
                      data-testid={`service-${category}-filter-serial-number`}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell>
                    <ColumnTextFilter
                      value={soldFilterDrafts.model}
                      onValueChange={(value) => updateSoldFilterDraft("model", value)}
                      onApply={(value) => applySoldFilter("model", value)}
                      placeholder="Filter…"
                      aria-label="Filter Model"
                      data-testid={`service-${category}-filter-model`}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell>
                    <ColumnPriceRangeFilter
                      value={{
                        min: soldFilterDrafts.service_cost_min,
                        max: soldFilterDrafts.service_cost_max,
                      }}
                      onValueChange={({ min, max }) => setSoldFilterDrafts((current) => ({
                        ...current,
                        service_cost_min: min,
                        service_cost_max: max,
                      }))}
                      onApply={({ min, max }) => applySoldFilters({
                        ...appliedSoldFilters,
                        service_cost_min: min,
                        service_cost_max: max,
                      })}
                      precision={2}
                      aria-label="Service Cost range"
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell>
                    <ColumnSelectFilter
                      value={soldFilterDrafts.dvt_status}
                      onValueChange={(value) => updateSoldFilterDraft("dvt_status", value)}
                      onApply={(value) => applySoldFilter("dvt_status", value)}
                      options={SOLD_DVT_OPTIONS}
                      placeholder="All"
                      aria-label="Filter DVT Warranty"
                      data-testid={`service-${category}-filter-dvt-status`}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell className="align-middle" aria-label="Actions filter controls">
                    <div className="flex h-7 w-full items-center justify-end">
                      <ClearAllFilters
                        onClick={clearSoldFilters}
                        disabled={
                          !appliedSearch
                          && !appliedSoldDateRange.from
                          && !appliedSoldDateRange.to
                          && !SOLD_FILTER_KEYS.some((key) => appliedSoldFilters[key])
                        }
                        data-testid={`service-${category}-filter-clear-all`}
                      />
                    </div>
                  </ColumnFilterCell>
                </ColumnFilterRow>
              ) : null}
            </thead>
            <tbody>
              <ListTableRefreshingRow
                active={loading && tickets.length > 0}
                colSpan={8}
                label="Refreshing Service tickets..."
                testId="service-ticket-refreshing"
              />
              {loading && tickets.length === 0 ? (
                <ListTableState colSpan={8} kind="loading">Loading Service tickets...</ListTableState>
              ) : error && tickets.length === 0 ? (
                <ListTableState
                  colSpan={8}
                  kind="error"
                  onRetry={() => setListRefreshKey((value) => value + 1)}
                  testId="service-ticket-list-error"
                >
                  {error}
                </ListTableState>
              ) : tickets.length === 0 ? (
                <ListTableState colSpan={8} kind="filtered-empty">
                  No tickets match the current filters.
                </ListTableState>
              ) : (
                tickets.map((ticket, index) => {
                  const queueAgingAlert = isQueueTicketOverdue(ticket, selectedStage);
                  const canMoveTicket = canShowServiceStandardMove({ role: user?.role, stage: ticket.stage });
                  const canEditTicket = getServiceEditCapabilities(user?.role, ticket.stage).canEditTicket;
                  const showsQuotationAction = serviceTicketShowsQuotationAction(ticket);
                  const canDeleteTicket = canDeleteServiceTicket(user?.role);
                  const rowActionCount = 2 + Number(canMoveTicket) + Number(canEditTicket) + Number(showsQuotationAction) + Number(canDeleteTicket);
                  const usesMoreActions = rowActionCount >= 3;
                  const showsMoveDirectly = canMoveTicket && rowActionCount >= 4;
                  return (
                  <tr
                    key={ticket.id}
                    className={queueAgingAlert ? QUEUE_AGING_ROW_CLASS : undefined}
                    data-queue-aging-alert={queueAgingAlert ? "true" : undefined}
                    data-testid={`service-ticket-row-${ticket.id}`}
                  >
                    <td className="num">
                      {(pagination.page - 1) * pagination.page_size + index + 1}
                    </td>
                    <td className="text-xs font-medium tabular-nums text-zinc-700 dark:text-zinc-300">
                      {formatServiceListDate(ticket.date_in)}
                    </td>
                    <td className="text-xs">
                      <DataValue value={ticket.owner?.name} />
                    </td>
                    <td className="max-w-[260px] text-xs font-semibold tracking-[0.02em]">
                      <DataValue value={ticket.device?.serial_number} containment="truncate" className="inline-block max-w-[150px] align-middle" />
                      {ticket.is_cannibal_stock ? (
                        <PassiveBadge tone="warning" className="ml-2 align-middle" data-testid={`service-cannibal-badge-${ticket.id}`}>
                          Cannibal
                        </PassiveBadge>
                      ) : null}
                      {ticket.stock_reference_status === "unknown" ? (
                        <PassiveBadge tone="warning" className="ml-2 align-middle" data-testid={`service-stock-reference-unknown-${ticket.id}`}>
                          UNK
                        </PassiveBadge>
                      ) : null}
                    </td>
                    <td className="text-xs">
                      <DataValue value={ticket.device?.model} />
                    </td>
                    <td className="text-xs">
                      <ServiceCostPresentation serviceCost={ticket.service_cost} compact serviceTable />
                    </td>
                    <td className="text-xs">
                      <ServiceDvtBadge status={ticket.warranty?.dvt?.status} />
                    </td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1.5">
                        <Button
                          type="button"
                          onClick={() => navigate(ticketRoute(ticket))}
                          aria-label={`View Service ticket ${ticket.ticket_number || ""}`.trim()}
                          title="View"
                          variant="info"
                          size="row-icon"
                          data-action-category="view-info"
                        >
                          <Eye className="h-4 w-4" strokeWidth={2} />
                        </Button>
                        {showsMoveDirectly ? (
                          <Button
                            type="button"
                            onClick={() => openMoveDialog(ticket)}
                            disabled={Boolean(moveLoadingTicketId)}
                            variant="navigation-forward"
                            aria-busy={moveLoadingTicketId === ticket.id}
                            aria-label={`Move Service ticket ${ticket.ticket_number || ""}`.trim()}
                            title={moveLoadingTicketId === ticket.id ? "Loading Service ticket..." : "Move"}
                            size="row-icon"
                            data-action-category="workflow-move"
                          >
                            <img
                              alt=""
                              aria-hidden="true"
                              className="h-4 w-auto"
                              src={flowpiMoveIcon}
                            />
                          </Button>
                        ) : null}
                        {!usesMoreActions ? (
                          <Button
                            type="button"
                            onClick={(event) => openStageHistory(ticket, event.currentTarget)}
                            aria-label={`View stage history for Service ticket ${ticket.ticket_number || ""}`.trim()}
                            title="View Stage History"
                            variant="info"
                            size="row-icon"
                            data-action-category="view-info"
                          >
                            <History strokeWidth={2} />
                          </Button>
                        ) : null}
                        {usesMoreActions ? <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              title="More actions"
                              aria-label={`More actions for Service ticket ${ticket.ticket_number || ""}`.trim()}
                              ref={(node) => {
                                if (node) moreActionTriggerRefs.current.set(ticket.id, node);
                                else moreActionTriggerRefs.current.delete(ticket.id);
                              }}
                              data-testid={`service-more-actions-${ticket.id}`}
                              variant="neutral"
                              size="row-icon"
                              data-action-category="menu-trigger"
                            >
                              <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {canMoveTicket && !showsMoveDirectly ? (
                              <DropdownMenuItem
                                onSelect={() => openMoveDialog(ticket)}
                                disabled={Boolean(moveLoadingTicketId)}
                                aria-busy={moveLoadingTicketId === ticket.id || undefined}
                                data-service-more-action="move"
                              >
                                <img alt="" aria-hidden="true" className="h-4 w-auto" src={flowpiMoveIcon} />
                                <span data-service-action-label>{moveLoadingTicketId === ticket.id ? "Loading Service ticket..." : "Move"}</span>
                              </DropdownMenuItem>
                            ) : null}
                            {canEditTicket ? (
                              <DropdownMenuItem
                                onSelect={() => navigate(ticketRoute(ticket, "edit"))}
                                data-service-more-action="edit"
                              >
                                <Edit3 className="h-3.5 w-3.5" strokeWidth={2} />
                                <span data-service-action-label>Edit</span>
                              </DropdownMenuItem>
                            ) : null}
                            {showsQuotationAction ? (
                              <DropdownMenuItem
                                onSelect={() => openQuotation(ticket)}
                                data-service-more-action="quotation"
                              >
                                <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                                <span data-service-action-label>{serviceTicketQuotationActionLabel(sqActions[ticket.id])}</span>
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem
                              onSelect={() => openStageHistory(ticket)}
                              data-service-more-action="history"
                            >
                              <History className="h-3.5 w-3.5" strokeWidth={2} />
                              <span data-service-action-label>View Stage History</span>
                            </DropdownMenuItem>
                            {canDeleteTicket ? (
                              <DropdownMenuItem
                                onSelect={() => openDeleteDialog(ticket)}
                                data-service-more-action="delete"
                                variant="destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                                <span data-service-action-label>Delete</span>
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu> : null}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </ListTable>
        </ListTableViewport>

        <ServiceListPagination
          page={pagination.page}
          pageSize={pageSize}
          total={pagination.total}
          totalPages={pagination.total_pages}
          loading={loading}
          onPrevious={() =>
            setSearchParams(
              buildListSearchParams({
                branch: selectedBranch,
                stage: selectedStage,
                search: appliedSearch,
                page: Math.max(1, page - 1),
                pageSize,
                soldFilters: appliedSoldFilters,
                dateRange: appliedSoldDateRange,
              }),
              { replace: true }
            )
          }
          onNext={() =>
            setSearchParams(
              buildListSearchParams({
                branch: selectedBranch,
                stage: selectedStage,
                search: appliedSearch,
                page: page + 1,
                pageSize,
                soldFilters: appliedSoldFilters,
                dateRange: appliedSoldDateRange,
              }),
              { replace: true }
            )
          }
          onPageSizeChange={(nextPageSize) =>
            setSearchParams(
              buildListSearchParams({
                branch: selectedBranch,
                stage: selectedStage,
                search: appliedSearch,
                page: 1,
                pageSize: nextPageSize,
                soldFilters: appliedSoldFilters,
                dateRange: appliedSoldDateRange,
              }),
              { replace: true }
            )
          }
        />
      </ListTableShell>

      <ServiceStageHistoryDialog
        open={Boolean(historyTicket)}
        loading={historyLoading}
        error={historyError}
        records={historyRecords}
        ticket={historyTicket}
        onClose={closeStageHistory}
      />

      {createModalOpen ? (
        <FlowpiDialogSurface
          open
          onClose={closeCreateModal}
          busy={createSubmitting}
          accessibleTitle="Device Registration"
          testId="service-create-modal"
        >
          <div className="flex h-[760px] max-h-[calc(100vh-48px)] w-[1040px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-zinc-200 px-7 py-5">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                  Service Intake
                </div>
                <h2 id="service-create-title" className="mt-1 text-2xl font-semibold text-zinc-900">
                  Device Registration
                </h2>
              </div>
              <Button
                type="button"
                onClick={closeCreateModal}
                variant="ghost"
                size="icon"
                data-action-category="close"
                data-testid="service-create-close"
                aria-label="Close Device Registration"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </Button>
            </div>

            <div className="border-b border-zinc-200 px-7 py-5">
              <div className="flex items-center">
                {CREATE_STEPS.map((step, index) => {
                  const active = index === createStep;
                  const complete = index < createStep;
                  return (
                    <React.Fragment key={step}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${
                            active || complete
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-zinc-200 bg-white text-zinc-500"
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <div
                            className={`text-sm font-semibold ${
                              active ? "text-zinc-900" : "text-zinc-500"
                            }`}
                          >
                            {step}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">
                            Step {index + 1}
                          </div>
                        </div>
                      </div>
                      {index < CREATE_STEPS.length - 1 ? (
                        <div
                          className={`mx-4 h-px flex-1 ${
                            index < createStep ? "bg-primary" : "bg-zinc-200"
                          }`}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
              {renderCreateStep()}
              {createNotice ? (
                <div className="mt-5 rounded-sm border border-warning bg-warning/10 px-4 py-3 text-sm text-zinc-700">
                  {createNotice}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-7 py-4">
              <Button
                type="button"
                onClick={goToPreviousCreateStep}
                disabled={createStep === 0}
                variant="navigation-back"
                data-action-category="navigation-back"
              >
                Previous
              </Button>
              {createStep < CREATE_STEPS.length - 1 ? (
                <Button
                  type="button"
                  onClick={goToNextCreateStep}
                  data-action-category="navigation-forward"
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleCreateTicket}
                  loading={createSubmitting}
                  loadingLabel="Creating..."
                  variant="success"
                  data-action-category="submit-confirm"
                >
                  Create Ticket
                </Button>
              )}
            </div>
          </div>
        </FlowpiDialogSurface>
      ) : null}
      <StageMoveDialog
        key={moveTicket ? `move-${moveTicket.id}` : "move-closed"}
        open={Boolean(moveTicket)}
        ticket={moveTicket}
        role={user?.role}
        onClose={() => setMoveTicket(null)}
        onMoved={() => setListRefreshKey((current) => current + 1)}
      />
      <ServiceExportDialog
        key={exportModalOpen ? "export-open" : "export-closed"}
        open={exportModalOpen}
        currentCategory={category}
        currentBranch={selectedBranch}
        currentStage={selectedStage}
        appliedSearch={appliedSearch}
        onClose={() => setExportModalOpen(false)}
      />
      <ServiceLifecycleDialog
        key={deleteTicket ? `delete-${deleteTicket.id}` : "delete-closed"}
        mode="delete"
        open={Boolean(deleteTicket)}
        ticket={deleteTicket}
        error={deleteError}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteTicket}
      />
    </OrdinaryPageBody>
  );
}
